import { lazy, Suspense, type ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ProtectedRoute } from '@/components';

type PageModule = { default: ComponentType };

const pascalToKebab = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const ownsPath = (prefixes: readonly string[], pathname: string) =>
  prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Prototypes that are a whole application rather than a single screen: they mount
 * their own nested router, so they own every path below their mount point.
 *
 * Two things follow from being on this list, which is why NON_PROTOTYPE_PREFIXES
 * derives from it rather than repeating it:
 *  - the route needs a `/*` splat, or the prototype's own sub-routes never match
 *  - the index and helper must skip it — there is no single screen to tweak, and
 *    its entry file backs the whole tree beneath it
 *
 * Such a prototype almost certainly belongs in FULL_BLEED_PREFIXES too, but that
 * is a separate property (chrome, not routing) and stays a separate list.
 */
export const APP_PROTOTYPE_PREFIXES = ['/dev/ai/vhc'] as const;

export const isAppPrototype = (pathname: string): boolean =>
  ownsPath(APP_PROTOTYPE_PREFIXES, pathname);

/**
 * /dev/ai routes that are NOT generated prototype pages. They have no
 * AIGenPage*.tsx behind them, so the index must not list them and the helper must
 * not offer Tweak/Copy/Delete against a file path that doesn't exist.
 *
 * Prefix match: an exact-match set covered `/dev/ai/vhc` but neither its splat nor
 * any of the ~50 screens below it, so the mount leaked back into both consumers —
 * the very thing listing it here is meant to prevent.
 */
export const NON_PROTOTYPE_PREFIXES = ['/dev/ai/design-system', ...APP_PROTOTYPE_PREFIXES];

export const isNonPrototypePath = (pathname: string): boolean =>
  pathname === '/dev/ai' || pathname === '/dev/ai/' || ownsPath(NON_PROTOTYPE_PREFIXES, pathname);

/**
 * Prototypes that render their OWN application chrome (sidebar, header). They
 * must not get the mock rail and rail offset AIRouteLayout adds to make a
 * single-screen prototype look in-context — otherwise the user sees two
 * sidebars: the host app's rail and the prototype's own.
 *
 * Prefix match, because such a prototype owns its sub-paths too.
 */
export const FULL_BLEED_PREFIXES = ['/dev/ai/vhc'] as const;

export const isFullBleedPrototype = (pathname: string): boolean =>
  FULL_BLEED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const getAIRoutes = (): RouteObject[] => {
  if (!import.meta.env.DEV && import.meta.env.VITE_TARGET !== 'dev') return [];

  // Lazy so the whole src/dev/PrototypeHelper tree is code-split into a dev-only
  // chunk. The gate above returns before this runs in production, so the dev
  // tooling never reaches the production bundle's static graph.
  const AIRouteLayout = lazy(() => import('@/dev/PrototypeHelper/AIRouteLayout'));
  const AIPrototypeIndex = lazy(() => import('@/dev/PrototypeHelper/AIPrototypeIndex'));
  const PrototypeError = lazy(() => import('@/dev/PrototypeHelper/PrototypeError'));

  // errorElement on the CHILD routes (not the layout) so a crash renders inside
  // the layout's <Outlet/> — the helper bar + open Tweak chat survive, letting
  // the user paste the error and have Claude fix it. Without this it bubbles to
  // the root and React Router blanks the whole page ("Unexpected Application Error").
  const errorElement = (
    <Suspense fallback={null}>
      <PrototypeError />
    </Suspense>
  );

  const modules = import.meta.glob<PageModule>('../pages/AIGenerated/AIGenPage*.tsx');
  const prototypeRoutes: RouteObject[] = Object.entries(modules)
    .flatMap(([filePath, loader]) => {
      const name = filePath.match(/AIGenPage(\w+)\.tsx$/)?.[1];
      if (!name) return [];
      const Page = lazy(loader);
      const path = `/dev/ai/${pascalToKebab(name)}`;
      const page = { element: <Page />, errorElement };
      // Only an app prototype gets the splat, and only because it mounts its own
      // nested <Routes> — React Router throws if you nest a second <Router>, so a
      // MemoryRouter is not an option and the sub-paths must match here.
      //
      // Giving it to every prototype made each single-screen page answer on a
      // second URL it has no use for: `/dev/ai/<slug>/*` matches a literal `*`
      // segment, so the index listed, and the Share button handed out, a working
      // duplicate of all 49 of them. A single-screen prototype has no sub-paths,
      // so anything below it should 404 like it used to.
      return isAppPrototype(path)
        ? [
            { path, ...page },
            { path: `${path}/*`, ...page },
          ]
        : [{ path, ...page }];
    })
    .sort((a, b) => a.path.localeCompare(b.path));

  return [
    {
      // Auth guard: the /dev/ai/* prototype tooling ships to the deployed `dev`
      // host (VITE_TARGET==='dev'), so without this wrapper every prototype —
      // and the AIPrototypeIndex that lists them — is reachable unauthenticated.
      // ProtectedRoute defaults to requireAuth and redirects to /auth, matching
      // the rest of the app. It sits ABOVE the layout so it covers the index and
      // all generated pages, plus the embed=1 iframe path.
      element: (
        <ProtectedRoute>
          <Suspense fallback={null}>
            <AIRouteLayout />
          </Suspense>
        </ProtectedRoute>
      ),
      children: [
        { path: '/dev/ai', element: <AIPrototypeIndex />, errorElement },
        ...prototypeRoutes,
      ],
    },
  ];
};

/**
 * The design-system showcase. Unlike the prototypes it is part of the app, not a mock of
 * it, so router.tsx mounts it inside <Layout>: the real sidebar and header take the reader
 * back into the product. Same dev-only gate as the prototypes; the splat is because the
 * showcase mounts its own per-entry routes (src/design-system/index.tsx).
 */
export const getDesignSystemRoutes = (): RouteObject[] => {
  if (!import.meta.env.DEV && import.meta.env.VITE_TARGET !== 'dev') return [];

  const DesignSystem = lazy(() => import('@/design-system'));
  return [
    {
      path: '/dev/ai/design-system/*',
      element: (
        <ProtectedRoute>
          <DesignSystem />
        </ProtectedRoute>
      ),
    },
  ];
};
